/**
 * Agent Prompt Constants
 *
 * Shared constants for the TipTap-based Prompt Editor used across frontend and backend.
 */

import { DbEnum } from "../../../generated/prisma-enums";
import type { VariableDefinition } from "./agent-prompt.types";

// ============================================================================
// TipTap Node Names
// ============================================================================

/**
 * TipTap custom node names for the PromptEditor
 * Use these constants instead of hardcoded strings for type safety and easy refactoring
 */
export const PROMPT_NODE_NAMES = {
  SECTION: "section",
  SECTION_TITLE: "sectionTitle",
  TAB_CONTAINER: "tabContainer",
  TAB_PANEL: "tabPanel",
  COLLECTION_MENTION: "collection-mention",
  VARIABLE_MENTION: "variable-mention",
  SKILL_MENTION: "skill-mention",
  PLACEHOLDER_MENTION: "placeholder-mention",
  // Block nodes
  BLOCKQUOTE: "blockquote",
  HEADING: "heading",
  // List nodes
  BULLET_LIST: "bulletList",
  ORDERED_LIST: "orderedList",
  TASK_LIST: "taskList",
  LIST_ITEM: "listItem",
  TASK_ITEM: "taskItem",
  // Core TipTap nodes (standard, not custom)
  PARAGRAPH: "paragraph",
  TEXT: "text",
  DOC: "doc",
  HARD_BREAK: "hardBreak",
  // Code block node
  CODE_BLOCK: "codeBlock"
} as const;

/**
 * TipTap mark names for the PromptEditor.
 *
 * `RAW` is not a formatting mark — it is the answer to "is this text node prose, or
 * is it markdown source?". The parser has no node for a GFM table or an authored
 * `<context>` block, so it stores their source verbatim in a text node. Without a
 * marker that node is indistinguishable from prose, and the serializer escapes it as
 * prose: `\` -> `\\` and `` ` `` -> `` \` `` turn n backslashes into 2n+1 on every
 * save, compounding until the prompt is unreadable (NEX-2564).
 *
 * A text node marked RAW is emitted byte-for-byte, which is what makes
 * `serialize ∘ parse` a fixed point. It must stay declared in the editor schema
 * (`RawMarkdown` extension) — an undeclared mark is dropped on load, and a dropped
 * RAW mark re-arms the cascade on the next save.
 */
export const PROMPT_MARK_NAMES = {
  BOLD: "bold",
  ITALIC: "italic",
  STRIKE: "strike",
  CODE: "code",
  RAW: "raw"
} as const;

export type PromptMarkName = (typeof PROMPT_MARK_NAMES)[keyof typeof PROMPT_MARK_NAMES];

// ============================================================================
// Deployment Constants
// ============================================================================

/**
 * Default deployment type - Nexus is always the first tab when deployment mode is enabled
 */
export const DEFAULT_DEPLOYMENT_TYPE = "NEXUS" as const;

/**
 * Default deployment display name - Nexus is always the first tab when deployment mode is enabled
 */
export const DEFAULT_DEPLOYMENT_DISPLAY_NAME = "Nexus" as const;

// ============================================================================
// Integration Mappings
// ============================================================================

/**
 * Single source of truth for integration display names
 * Used across frontend and backend for consistent naming
 */
export const INTEGRATION_DISPLAY_NAMES: Record<DbEnum.DeploymentType, string> = {
  [DbEnum.DeploymentType.SLACK]: "Slack",
  [DbEnum.DeploymentType.TWILIO_SMS]: "SMS",
  [DbEnum.DeploymentType.TWILIO_VOICE]: "Twilio Voice",
  [DbEnum.DeploymentType.TEAMS]: "Teams",
  [DbEnum.DeploymentType.WHATSAPP]: "WhatsApp",
  [DbEnum.DeploymentType.EMBED]: "Embed",
  [DbEnum.DeploymentType.TELEGRAM]: "Telegram",
  [DbEnum.DeploymentType.GOOGLE_SHEETS]: "Google Sheets",
  [DbEnum.DeploymentType.GMAIL]: "Gmail",
  [DbEnum.DeploymentType.IMAP]: "IMAP",
  [DbEnum.DeploymentType.SMTP]: "SMTP",
  [DbEnum.DeploymentType.FB_MESSENGER]: "Messenger",
  [DbEnum.DeploymentType.INSTAGRAM]: "Instagram",
  [DbEnum.DeploymentType.EXCEL_ADDIN]: "Excel",
  [DbEnum.DeploymentType.WORD_ADDIN]: "Word",
  [DbEnum.DeploymentType.POWERPOINT_ADDIN]: "PowerPoint",
  [DbEnum.DeploymentType.OUTLOOK_ADDIN]: "Outlook Add-In",
  [DbEnum.DeploymentType.API]: "API",
  [DbEnum.DeploymentType.OUTLOOK]: "Outlook",
  [DbEnum.DeploymentType.AIRTABLE]: "Airtable",
  [DbEnum.DeploymentType.GOOGLE_MEET]: "Google Meet",
  [DbEnum.DeploymentType.ZOOM]: "Zoom"
};

/**
 * Deployment type mappings for integration types
 * Maps Prisma IntegrationType to deployment context strings
 * Used by backend to determine prompt content and formatting rules
 *
 * @example
 * - OUTLOOK/OUTLOOKADDIN -> EMAIL (same deployment context)
 * - TWILIO -> SMS (Twilio is an SMS provider)
 * - WHATSAPP -> WHATSAPP (direct mapping)
 *
 * Maps deployment types to their prompt tab context key.
 * The context key is used by the prompt editor to show deployment-specific sections.
 */
export const INTEGRATION_DEPLOYMENT_MAPPINGS: Partial<Record<DbEnum.DeploymentType, string>> = {
  [DbEnum.DeploymentType.WHATSAPP]: "WHATSAPP",
  [DbEnum.DeploymentType.GMAIL]: "EMAIL",
  [DbEnum.DeploymentType.OUTLOOK]: "EMAIL",
  [DbEnum.DeploymentType.OUTLOOK_ADDIN]: "EMAIL",
  [DbEnum.DeploymentType.TWILIO_SMS]: "SMS",
  [DbEnum.DeploymentType.SLACK]: "SLACK",
  [DbEnum.DeploymentType.TEAMS]: "TEAMS",
  [DbEnum.DeploymentType.TELEGRAM]: "TELEGRAM",
  [DbEnum.DeploymentType.TWILIO_VOICE]: "PHONE_CALL",
  [DbEnum.DeploymentType.GOOGLE_SHEETS]: "GOOGLESHEET",
  [DbEnum.DeploymentType.API]: "API",
  // Embed is the Nexus web widget — it uses the same prompt content as NEXUS.
  // Mapping to "NEXUS" means: (1) backend resolves Embed to NEXUS tab content,
  // (2) frontend filters it from the tab bar (any type mapped to NEXUS is redundant).
  [DbEnum.DeploymentType.EMBED]: "NEXUS",
  [DbEnum.DeploymentType.EXCEL_ADDIN]: "EXCEL",
  [DbEnum.DeploymentType.POWERPOINT_ADDIN]: "POWERPOINT",
  [DbEnum.DeploymentType.WORD_ADDIN]: "WORD",
  [DbEnum.DeploymentType.FB_MESSENGER]: "FBMESSENGER",
  [DbEnum.DeploymentType.INSTAGRAM]: "INSTAGRAM",
  // Email providers
  [DbEnum.DeploymentType.IMAP]: "EMAIL",
  [DbEnum.DeploymentType.SMTP]: "EMAIL",
  // Productivity integrations
  [DbEnum.DeploymentType.AIRTABLE]: "AIRTABLE",
  // Video conferencing
  [DbEnum.DeploymentType.GOOGLE_MEET]: "GOOGLE_MEET",
  [DbEnum.DeploymentType.ZOOM]: "ZOOM"
};

/**
 * Display names for deployment context keys (channel-level labels).
 * Context keys are the deduplicated values from INTEGRATION_DEPLOYMENT_MAPPINGS.
 * These provide stable labels regardless of which provider is active.
 */
export const DEPLOYMENT_CONTEXT_DISPLAY_NAMES: Record<string, string> = {
  WHATSAPP: "WhatsApp",
  EMAIL: "Email",
  SMS: "SMS",
  SLACK: "Slack",
  TEAMS: "Teams",
  TELEGRAM: "Telegram",
  PHONE_CALL: "Phone Call",
  GOOGLESHEET: "Google Sheets",
  API: "API",
  EXCEL: "Excel",
  POWERPOINT: "PowerPoint",
  WORD: "Word",
  FBMESSENGER: "Messenger",
  INSTAGRAM: "Instagram",
  AIRTABLE: "Airtable",
  GOOGLE_MEET: "Google Meet",
  ZOOM: "Zoom"
};

// NEXUS is the default tab context used by Embed (see INTEGRATION_DEPLOYMENT_MAPPINGS:
// EMBED -> "NEXUS"). Embed carries customer identity, so customer.* variables must be
// offered on the default tab — even though "NEXUS" is intentionally absent from
// DEPLOYMENT_CONTEXT_DISPLAY_NAMES to keep it out of the channel tab bar.
export const CUSTOMER_IDENTITY_DEPLOYMENTS = [
  ...Object.keys(DEPLOYMENT_CONTEXT_DISPLAY_NAMES),
  DEFAULT_DEPLOYMENT_TYPE
];

// ============================================================================
// Variable Definitions
// ============================================================================

/**
 * Available Variables for the Prompt Editor
 *
 * This list defines all variables that users can insert into their prompts.
 * Each variable is replaced with a runtime value on the backend during AI execution.
 *
 * Variable Format: {{variableName}}
 * Example: {{conversationId}} -> "Conversation ID: abc123"
 */
export const AVAILABLE_VARIABLES: VariableDefinition[] = [
  {
    id: "conversationId",
    name: "conversationId",
    displayName: "Conversation ID",
    description: "Unique identifier for the current conversation",
    icon: "hashtag"
  },
  {
    id: "currentDateTime",
    name: "currentDateTime",
    displayName: "Current Date",
    description: "Current date and time at execution",
    icon: "calendar"
  },
  {
    id: "firstName",
    name: "firstName",
    displayName: "First Name",
    description: "First name of the agent",
    icon: "user"
  },
  {
    id: "lastName",
    name: "lastName",
    displayName: "Last Name",
    description: "Last name of the agent",
    icon: "user"
  },
  {
    id: "role",
    name: "role",
    displayName: "Role",
    description: "Role/job title of the agent",
    icon: "briefcase"
  },
  // Inbox-specific variables
  {
    id: "inboxStatus",
    name: "inboxStatus",
    displayName: "Conversation Status",
    description: "Current status of the inbox conversation",
    icon: "status"
  },
  {
    id: "inboxComments",
    name: "inboxComments",
    displayName: "Agent Notes",
    description: "Notes and comments left by human agents",
    icon: "comment"
  },
  {
    id: "assignedUserName",
    name: "assignedUserName",
    displayName: "Assigned Agent",
    description: "Name of the human agent assigned to the conversation",
    icon: "user"
  },
  // Customer identity variables — scoped to non-NEXUS deployments only
  {
    id: "customerName",
    name: "customerName",
    displayName: "Customer Name",
    description: "Display name of the customer in the conversation",
    icon: "user",
    deployments: CUSTOMER_IDENTITY_DEPLOYMENTS
  },
  {
    id: "customerPhone",
    name: "customerPhone",
    displayName: "Customer Phone",
    description:
      "Primary phone number from the customer's profile (may differ from the channel identifier)",
    icon: "phone",
    deployments: CUSTOMER_IDENTITY_DEPLOYMENTS
  },
  {
    id: "customerEmail",
    name: "customerEmail",
    displayName: "Customer Email",
    description: "Primary email address of the customer",
    icon: "mail",
    deployments: CUSTOMER_IDENTITY_DEPLOYMENTS
  },
  {
    id: "customerIdentifier",
    name: "customerIdentifier",
    displayName: "Customer Identifier",
    description:
      "Channel-specific contact identifier of the customer (phone, email, or other ID depending on the deployment)",
    icon: "hashtag",
    deployments: CUSTOMER_IDENTITY_DEPLOYMENTS
  },
  {
    id: "customerExternalId",
    name: "customerExternalId",
    displayName: "Customer External ID",
    description:
      "Business's own user identifier for this customer (set via embed widget or WhatsApp redirect)",
    icon: "hashtag",
    deployments: CUSTOMER_IDENTITY_DEPLOYMENTS
  },
  {
    id: "sendNotes",
    name: "sendNotes",
    displayName: "Send Notes",
    description:
      "Summary notes from previous voice call sessions with this customer, or workflow-provided notes for outbound calls",
    icon: "notes",
    deployments: ["PHONE_CALL"]
  }
];

/**
 * List of valid variable names that can be resolved at runtime.
 * Variables not in this list will be marked as invalid (isValid: false).
 */
export const VALID_VARIABLE_NAMES = AVAILABLE_VARIABLES.map((v) => v.name);
